// Google web search run in the browser with Google's Programmable Search
// Element (cse.js) - the search API for servers (Custom Search JSON API) is
// closed to new projects and answers 403, but the element works for anyone.
// The results go to the backend with the chat message (see api.js and
// backend/app/routers/chat.py), and the element itself is shown under the
// reply (GoogleResults in App.js) - Google's terms ask for its results to be
// displayed as they come.

const CX = process.env.REACT_APP_GOOGLE_CSE_CX || 'f569ef295e94f411e';
const GNAME = 'wheniseeus-web';
const MAX_RESULTS = 3;
const SEARCH_TIMEOUT_MS = 8000;

// One results element for the whole app, moved under whichever reply used
// the latest search. Parked in a hidden holder while no reply shows it.
let resultsNode = null;
let holder = null;
let elementReady = null;
let pending = null;

function stripTags(html) {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return (div.textContent || '').trim();
}

// Result links come wrapped in a google.com/url?q=<real link> redirect.
function realLink(link) {
  try {
    const url = new URL(link);
    if (url.hostname.endsWith('google.com') && url.pathname === '/url') {
      return url.searchParams.get('q') || url.searchParams.get('url') || link;
    }
  } catch {
    // not a URL - filtered out by the caller
  }
  return link;
}

function toResult(r) {
  const link = realLink(r.unescapedUrl || r.url || '');
  return {
    title: (r.titleNoFormatting || stripTags(r.title)).slice(0, 300),
    snippet: (r.contentNoFormatting || stripTags(r.content)).slice(0, 1000),
    link
  };
}

function loadElement() {
  if (elementReady) return elementReady;
  elementReady = new Promise((resolve, reject) => {
    holder = document.createElement('div');
    holder.style.display = 'none';
    document.body.appendChild(holder);
    resultsNode = document.createElement('div');
    resultsNode.className = 'google-results-box';
    holder.appendChild(resultsNode);

    window.__gcse = {
      parsetags: 'explicit',
      initializationCallback: () => {
        // No overlay: the engine's "Overlay" layout would pop the results up
        // over the page on every search instead of leaving them in the box.
        window.google.search.cse.element.render({
          div: resultsNode,
          tag: 'searchresults-only',
          gname: GNAME,
          attributes: { overlayResults: false }
        });
        resolve();
      },
      searchCallbacks: {
        web: {
          ready: (gname, query, promos, results) => {
            if (gname !== GNAME || !pending) return;
            pending.resolve((results || []).map(toResult).filter((r) => /^https?:\/\//.test(r.link)).slice(0, MAX_RESULTS));
            pending = null;
          }
        }
      }
    };

    const script = document.createElement('script');
    script.src = `https://cse.google.com/cse.js?cx=${encodeURIComponent(CX)}`;
    script.async = true;
    script.onerror = () => {
      elementReady = null;
      reject(new Error('Google search element failed to load'));
    };
    document.head.appendChild(script);
  });
  return elementReady;
}

// [{ title, snippet, link }] for the query - [] if the element can't load,
// finds nothing or takes too long (the chat then answers without a search).
export async function googleSearch(query) {
  try {
    await loadElement();
  } catch {
    return [];
  }
  pending?.resolve([]);
  return new Promise((resolve) => {
    const giveUp = setTimeout(() => {
      if (pending?.resolve === finish) pending = null;
      resolve([]);
    }, SEARCH_TIMEOUT_MS);
    const finish = (results) => {
      clearTimeout(giveUp);
      resolve(results);
    };
    pending = { resolve: finish };
    window.google.search.cse.element.getElement(GNAME).execute(query);
  });
}

// Moves the results element into `container` (a reply's results box), and
// back to the hidden holder when that reply no longer shows it.
export function attachGoogleResults(container) {
  if (!resultsNode || !container) return () => {};
  container.appendChild(resultsNode);
  return () => {
    if (holder && resultsNode.parentNode === container) holder.appendChild(resultsNode);
  };
}
